import type { ActiveAbility } from './types.js';
import { DESCRIPTION_HARD_MAX } from './frontmatter.js';

/**
 * One-line, whitespace-collapsed description for the catalog.
 *
 * The catalog line is the ONLY thing the model reads before deciding which
 * skill to invoke (bodies load on Skill(slug)), so it renders the description in
 * FULL. The only cap is the authoring contract's own ceiling
 * (DESCRIPTION_HARD_MAX, imported — never a second number): a description that
 * passed the lint is never silently truncated here, and a legacy row over the
 * ceiling is cut at exactly the ceiling so the two can never disagree.
 * (Until 2026-08-22 this capped at 120 chars, which cut every system skill's
 * trigger phrases out of the prompt — the model saw labels, not triggers.)
 */
function concise(desc: string | null | undefined, cap = DESCRIPTION_HARD_MAX): string {
  return capped((desc ?? '').replace(/\s+/g, ' ').trim(), cap);
}

/** The cap half of `concise`, for text already collapsed (the budget probes re-render many times). */
function capped(d: string, cap: number): string {
  if (!d) return '';
  return d.length > cap ? `${d.slice(0, cap - 1).trimEnd()}…` : d;
}

/**
 * SKILL_CATALOG_BUDGET_V1 (harness to nine, X3): what the catalog may spend.
 * The caller resolves the serving model's window and freezes `lastUsed` for the
 * session, so the same inputs give the same bytes and the prompt cache holds.
 */
export interface CatalogBudget {
  /** The serving model's context window, in tokens. */
  contextWindowTokens: number;
  /** Item slug to last use (epoch ms). A missing slug counts as never used. */
  lastUsed?: Readonly<Record<string, number>>;
  /**
   * Tokens of the list's budget already spent by text rendered beside it (CROSS_TASK_CACHE_V1:
   * the Agent roster block), so the two together stay inside the 2%. Absent ⇒ 0.
   */
  reservedTokens?: number;
}

/** The list gets 2% of the window, never under 2000 tokens. */
export function catalogBudgetTokens(contextWindowTokens: number): number {
  return Math.max(2_000, Math.floor(contextWindowTokens * 0.02));
}

/** Four characters a token, rounded up: the engine's standard estimate, deterministic. */
export function estimateCatalogTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/** Stage 1 never trims a description shorter than this; past it, stage 2 takes over. */
const TRIM_FLOOR = 200;

const CATALOG_HEADER = '## Active abilities — your installed capabilities; reach into these FIRST';

interface CatalogItem {
  kind: 'skill' | 'agent';
  slug: string;
  /** Whitespace-collapsed once, capped at the contract ceiling. */
  desc: string;
  /** The owning ability's drop rank (TIER_DROP_RANK): lower gives way first. */
  tier: number;
}

/**
 * Which tier gives way first when the list is over budget: bundled skills
 * (system and marketplace) first, then the company's (customer), and the
 * person's own (user) last. Their own skills are the most protected: nothing
 * else in the list stands in for them. An unknown scope ranks with the bundled.
 */
const TIER_DROP_RANK: Readonly<Record<string, number>> = { system: 0, marketplace: 0, customer: 1, user: 2 };

/**
 * The render, parameterised by the three budget stages: `cap` trims every
 * description (stage 1), `bare` lists items by name only (stage 2), `gone`
 * leaves items out behind a count line (stage 3). No budget means cap = the
 * contract ceiling and empty sets, which is exactly the pre-budget render.
 */
function renderCatalog(
  names: string[],
  groups: CatalogItem[][],
  cap = DESCRIPTION_HARD_MAX,
  bare: ReadonlySet<CatalogItem> = new Set(),
  gone: ReadonlySet<CatalogItem> = new Set(),
): string {
  const blocks: string[] = [];
  groups.forEach((group, i) => {
    const items: string[] = [];
    for (const it of group) {
      if (gone.has(it)) continue;
      const d = bare.has(it) ? '' : capped(it.desc, cap);
      items.push(`- ${it.kind} \`${it.slug}\`${d ? ` — ${d}` : ''}`);
    }
    if (items.length > 0) blocks.push(`### ${names[i]}\n${items.join('\n')}`);
    // Every item left out: the ability keeps its header line, so the model still
    // knows it is installed and can call Skill for it.
    else if (group.length > 0) blocks.push(`### ${names[i]}`);
  });
  if (blocks.length === 0 && gone.size === 0) return '';
  if (gone.size > 0) {
    const noun = [...gone].some((it) => it.kind === 'agent') ? 'skills and agents' : 'skills';
    blocks.push(
      `…and ${gone.size} more installed ${noun} not listed here, to save context. ` +
        'Call Skill with the slug you expect; an unknown slug replies with the full list.',
    );
  }
  return [CATALOG_HEADER, ...blocks].join('\n\n');
}

/** Smallest n in [lo, hi] with fits(n), assuming fits is monotone; hi when none is smaller. */
function smallestFitting(lo: number, hi: number, fits: (n: number) => boolean): number {
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) hi = mid;
    else lo = mid + 1;
  }
  return hi;
}

/**
 * Fit the render to the budget, in the X3 drop order: longest descriptions
 * trimmed first (one shared cap, lowered until it fits, never under
 * TRIM_FLOOR), then items down to name only, then those same items left out
 * with a count line (an ability whose items all left keeps its header line).
 * Items give way tier by tier (TIER_DROP_RANK: bundled, then the company's,
 * then the person's own); within a tier the least recently used go first, and
 * ties in recency go to the item listed later. Pure: the same inputs give the
 * same bytes.
 */
function budgetedCatalog(names: string[], groups: CatalogItem[][], budget: CatalogBudget): string {
  const limit = Math.max(0, catalogBudgetTokens(budget.contextWindowTokens) - (budget.reservedTokens ?? 0));
  const fits = (text: string) => estimateCatalogTokens(text) <= limit;
  const full = renderCatalog(names, groups);
  if (fits(full)) return full;

  const items = groups.flat();
  const longest = Math.max(...items.map((it) => it.desc.length));
  const floor = Math.min(TRIM_FLOOR, longest);
  // Stage 1: the largest cap that fits (a lower cap never renders longer).
  if (fits(renderCatalog(names, groups, floor))) {
    const cap = longest - smallestFitting(0, longest - floor, (n) => fits(renderCatalog(names, groups, longest - n)));
    return renderCatalog(names, groups, cap);
  }
  const used = (it: CatalogItem) => budget.lastUsed?.[it.slug] ?? Number.NEGATIVE_INFINITY;
  const order = items
    .map((it, pos) => ({ it, pos }))
    .sort((a, b) => a.it.tier - b.it.tier || used(a.it) - used(b.it) || b.pos - a.pos)
    .map(({ it }) => it);
  const first = (n: number) => new Set(order.slice(0, n));
  // Stage 2: the fewest least-recent items down to name only.
  const bareFits = (n: number) => fits(renderCatalog(names, groups, floor, first(n)));
  if (bareFits(order.length)) return renderCatalog(names, groups, floor, first(smallestFitting(1, order.length, bareFits)));
  // Stage 3: everyone is name only; leave out the fewest least-recent items.
  const all = first(order.length);
  const goneFits = (n: number) => fits(renderCatalog(names, groups, floor, all, first(n)));
  return renderCatalog(names, groups, floor, all, first(smallestFitting(1, order.length, goneFits)));
}

/**
 * Render installed abilities as an ABILITY-GROUPED catalog — "Ability → its
 * skills/agents" — so the model's primary capability lens is the ability, not a
 * flat skill menu. This is the presentation half of the ability-first
 * resolution order (active abilities → their items → search → built-ins).
 *
 * Shared by BOTH harnesses so the grouping format can't drift between arms.
 * Descriptions are kept to one capped line (the full ability CLAUDE.md bodies
 * already carry the detail; this is the quick-reference index). Returns '' when
 * nothing is installed, so no empty section is appended.
 *
 * `budget` (SKILL_CATALOG_BUDGET_V1) fits the list to 2% of the model's window;
 * absent, the output is byte-identical to the pre-budget render.
 */
export function formatAbilityGroupedCatalog(active: ActiveAbility[], budget?: CatalogBudget): string {
  const names: string[] = [];
  const groups: CatalogItem[][] = [];
  for (const a of active) {
    names.push(a.ability.name);
    const tier = TIER_DROP_RANK[a.ability.scope] ?? 0;
    groups.push([
      ...a.skills.map((sk) => ({ kind: 'skill' as const, slug: sk.slug, desc: concise(sk.description), tier })),
      ...a.agents.map((ag) => ({ kind: 'agent' as const, slug: ag.slug, desc: concise(ag.description), tier })),
    ]);
  }
  return budget ? budgetedCatalog(names, groups, budget) : renderCatalog(names, groups);
}

/**
 * SKILL_CATALOG_BUDGET_V1: ability instruction fragments get their own 2% of the
 * window. Whole fragments, in order; one that would overflow is left out, since a
 * cut instruction reads worse than a missing one.
 */
export function budgetInstructionFragments(fragments: readonly string[], budget: CatalogBudget): string {
  const limit = catalogBudgetTokens(budget.contextWindowTokens);
  const kept: string[] = [];
  for (const f of fragments) {
    if (f && estimateCatalogTokens([...kept, f].join('\n\n')) <= limit) kept.push(f);
  }
  return kept.join('\n\n');
}
